/**
 * CSV export control for the paid-media dashboard.
 *
 * Container that reads the hook's state and renders presentational buttons
 * based on export state.
 */
import React from 'react';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { useCsvExport } from 'src/hooks/useCsvExport';

interface ExportButtonProps {
  view: string;
  subView: string;
  filters: Record<string, string | string[] | undefined>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const ExportIdleButton: React.FC<{ label: string; onExport: () => void }> = ({ label, onExport }) => (
  <Button data-testid="export-csv-button" size="small" type="tertiary" onClick={onExport}>
    {label}
  </Button>
);

const ExportProgressButton: React.FC<{ label: string }> = ({ label }) => (
  <Button data-testid="export-csv-button" disabled size="small" type="tertiary">
    <Row alignmentVertical="center" spacing="200">
      <Loader size="small" />
      <Text type="b100">{label}</Text>
    </Row>
  </Button>
);

const ExportDownloadButton: React.FC<{ label: string; onDownload: () => void }> = ({ label, onDownload }) => (
  <Button
    data-testid="export-download-button"
    size="small"
    type="primary"
    onClick={onDownload}
  >
    {label}
  </Button>
);

const ExportErrorButton: React.FC<{ message: string; retryLabel: string; onRetry: () => void }> = ({ message, retryLabel, onRetry }) => (
  <Row alignmentVertical="center" spacing="200">
    <Text color="error" data-testid="export-error-message" type="b100">{message}</Text>
    <Button data-testid="export-retry-button" size="small" type="tertiary" onClick={onRetry}>
      {retryLabel}
    </Button>
  </Row>
);

export const ExportButton: React.FC<ExportButtonProps> = ({ view, subView, filters, sortBy, sortOrder }) => {
  const { t } = useTranslation();
  const { state, start, download, retry } = useCsvExport({ view, subView, filters, sortBy, sortOrder });

  switch (state) {
    case 'complete':
      return <ExportDownloadButton label={t('paid_media_export_download')} onDownload={download} />;
    case 'error':
      return <ExportErrorButton message={t('paid_media_export_error')} retryLabel={t('paid_media_export_retry')} onRetry={retry} />;
    case 'exporting':
      return <ExportProgressButton label={t('paid_media_export_preparing')} />;
    default:
      return <ExportIdleButton label={t('paid_media_export_button')} onExport={start} />;
  }
};
